import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  FileText,
  Link2,
  RefreshCcw,
  Users,
  Zap,
} from 'lucide-react';
import {
  Card,
  CardTitle,
  EmptyState,
  MetricCard,
  PageHeader,
  PageSpinner,
  SkeletonKpiCard,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { api } from '@/lib/api/client';
import { unwrap } from '@/lib/api/envelope';
import type { ResponseEnvelope } from '@/types/api';
import { Endpoints } from '@/lib/api/endpoints';

interface ConnectOverview {
  connectedClients: number;
  activeIntegrations: number;
  syncedProfiles: number;
  failedSyncs: number;
  enrichments: number;
  highRiskProfiles: number;
}

interface ConnectIntegration {
  id: string;
  clientId: string;
  clientName?: string;
  displayName: string;
  integrationType: string;
  status: string;
  triggerEvent?: string;
  lastSyncAt?: string;
  createdAt: string;
}

interface IntegrationPage {
  content: ConnectIntegration[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

interface ConnectSyncEvent {
  id: string;
  clientId: string;
  integrationId: string;
  outcome: string;
  httpStatusCode?: number;
  recordsAttempted: number;
  recordsDelivered: number;
  recordsFailed: number;
  durationMs?: number;
  createdAt: string;
}

interface SyncEventPage {
  content: ConnectSyncEvent[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

const SYNC_OUTCOME_COLOR: Record<string, string> = {
  SUCCESS: 'bg-emerald-500/15 text-emerald-400',
  PARTIAL: 'bg-amber-500/15 text-amber-400',
  FAILED: 'bg-rose-500/15 text-rose-400',
  SKIPPED: 'bg-surface-muted text-fg-muted',
};

const STATUS_COLOR: Record<string, string> = {
  ACTIVE: 'bg-emerald-500/15 text-emerald-400',
  INACTIVE: 'bg-surface-muted text-fg-muted',
  ERROR: 'bg-rose-500/15 text-rose-400',
};

type ActiveTab = 'integrations' | 'sync-events';

export function AdminConnectPage() {
  const [tab, setTab] = useState<ActiveTab>('integrations');
  const [intPage, setIntPage] = useState(0);
  const [syncPage, setSyncPage] = useState(0);

  const overviewQuery = useQuery({
    queryKey: ['admin', 'connect', 'overview'],
    queryFn: async () =>
      unwrap(await api.get<ResponseEnvelope<ConnectOverview>>(Endpoints.adminConnect.overview())),
    staleTime: 60_000,
  });

  const integrationsQuery = useQuery({
    queryKey: ['admin', 'connect', 'integrations', intPage],
    queryFn: async () =>
      unwrap(
        await api.get<ResponseEnvelope<IntegrationPage>>(Endpoints.adminConnect.integrations(), {
          params: { page: intPage, size: 25 },
        }),
      ),
    staleTime: 30_000,
  });

  const syncQuery = useQuery({
    queryKey: ['admin', 'connect', 'sync-events', syncPage],
    queryFn: async () =>
      unwrap(
        await api.get<ResponseEnvelope<SyncEventPage>>(
          '/v1/api/dashboard/connect/sync-history',
          { params: { page: syncPage, size: 25 } },
        ),
      ),
    enabled: tab === 'sync-events',
    staleTime: 30_000,
  });

  const ov = overviewQuery.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Connect Intelligence"
        subtitle="CRM synchronization activity, customer identity profiles, and enrichment analytics"
        badge={
          <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-500/15 px-3 py-1 text-xs font-semibold text-blue-400">
            <Link2 className="h-3 w-3" />
            Connect
          </span>
        }
      />

      {/* KPI Strip */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-6">
        {overviewQuery.isLoading ? (
          Array.from({ length: 6 }).map((_, i) => <SkeletonKpiCard key={i} />)
        ) : (
          <>
            <MetricCard label="Connected Clients" value={ov?.connectedClients ?? 0} icon={Users} />
            <MetricCard label="Active Integrations" value={ov?.activeIntegrations ?? 0} icon={Link2} />
            <MetricCard label="Synced Profiles" value={ov?.syncedProfiles ?? 0} icon={Activity} tone="success" />
            <MetricCard label="Failed Syncs" value={ov?.failedSyncs ?? 0} icon={AlertTriangle} tone="danger" />
            <MetricCard label="Enrichments" value={ov?.enrichments ?? 0} icon={Zap} tone="warning" />
            <MetricCard label="High Risk Profiles" value={ov?.highRiskProfiles ?? 0} icon={AlertTriangle} tone="danger" />
          </>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-2 rounded-xl border border-border bg-surface p-1" style={{ width: 'fit-content' }}>
        {(['integrations', 'sync-events'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-lg px-4 py-1.5 text-sm font-medium transition-colors ${
              tab === t
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-fg-muted hover:text-fg'
            }`}
          >
            {t === 'integrations' ? 'Integrations' : 'Sync Events'}
          </button>
        ))}
      </div>

      {/* Integrations Table */}
      {tab === 'integrations' && (
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <CardTitle>
              <span className="flex items-center gap-2">
                <Link2 className="h-4 w-4 text-fg-muted" />
                All Integrations
              </span>
            </CardTitle>
            {integrationsQuery.data && (
              <span className="text-xs text-fg-muted">
                {integrationsQuery.data.totalElements.toLocaleString()} total
              </span>
            )}
          </div>

          {integrationsQuery.isLoading ? (
            <PageSpinner />
          ) : !integrationsQuery.data?.content?.length ? (
            <EmptyState message="No CRM integrations configured yet." icon={Link2} />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <THead>
                    <TR>
                      <TH>Client</TH>
                      <TH>Name</TH>
                      <TH>Type</TH>
                      <TH>Status</TH>
                      <TH>Trigger</TH>
                      <TH>Last Sync</TH>
                      <TH>Created</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {integrationsQuery.data.content.map((i) => (
                      <TR key={i.id}>
                        <TD className="text-sm font-medium">{i.clientName ?? i.clientId}</TD>
                        <TD className="text-sm">{i.displayName}</TD>
                        <TD>
                          <span className="rounded bg-surface-muted px-2 py-0.5 font-mono text-[11px]">
                            {i.integrationType}
                          </span>
                        </TD>
                        <TD>
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLOR[i.status] ?? 'bg-surface-muted text-fg-muted'}`}
                          >
                            {i.status}
                          </span>
                        </TD>
                        <TD className="text-xs text-fg-muted">{i.triggerEvent ?? '—'}</TD>
                        <TD className="text-xs text-fg-muted">
                          {i.lastSyncAt ? new Date(i.lastSyncAt).toLocaleString() : '—'}
                        </TD>
                        <TD className="text-xs text-fg-muted">
                          {new Date(i.createdAt).toLocaleDateString()}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
              <Pagination page={intPage} totalPages={integrationsQuery.data.totalPages} onChange={setIntPage} />
            </>
          )}
        </Card>
      )}

      {/* Sync Events Table */}
      {tab === 'sync-events' && (
        <Card>
          <div className="mb-4 flex items-center justify-between">
            <CardTitle>
              <span className="flex items-center gap-2">
                <RefreshCcw className="h-4 w-4 text-fg-muted" />
                Sync Events
              </span>
            </CardTitle>
            {syncQuery.data && (
              <span className="text-xs text-fg-muted">
                {syncQuery.data.totalElements.toLocaleString()} total
              </span>
            )}
          </div>

          {syncQuery.isLoading ? (
            <PageSpinner />
          ) : !syncQuery.data?.content?.length ? (
            <EmptyState message="No sync events recorded yet." icon={FileText} />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <THead>
                    <TR>
                      <TH>Client</TH>
                      <TH>Integration</TH>
                      <TH>Outcome</TH>
                      <TH>HTTP</TH>
                      <TH>Delivered</TH>
                      <TH>Failed</TH>
                      <TH>Duration</TH>
                      <TH>Date</TH>
                    </TR>
                  </THead>
                  <TBody>
                    {syncQuery.data.content.map((e) => (
                      <TR key={e.id}>
                        <TD className="text-sm">{e.clientId}</TD>
                        <TD className="font-mono text-xs text-fg-muted">{e.integrationId}</TD>
                        <TD>
                          <span
                            className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${SYNC_OUTCOME_COLOR[e.outcome] ?? 'bg-surface-muted text-fg-muted'}`}
                          >
                            {e.outcome}
                          </span>
                        </TD>
                        <TD className="text-center text-sm">{e.httpStatusCode ?? '—'}</TD>
                        <TD className="text-center text-sm text-emerald-400">{e.recordsDelivered}</TD>
                        <TD className="text-center text-sm text-rose-400">{e.recordsFailed}</TD>
                        <TD className="text-xs text-fg-muted">
                          {e.durationMs != null ? `${e.durationMs}ms` : '—'}
                        </TD>
                        <TD className="text-xs text-fg-muted">
                          {new Date(e.createdAt).toLocaleString()}
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </div>
              <Pagination page={syncPage} totalPages={syncQuery.data.totalPages} onChange={setSyncPage} />
            </>
          )}
        </Card>
      )}
    </div>
  );
}

function Pagination({
  page,
  totalPages,
  onChange,
}: {
  page: number;
  totalPages: number;
  onChange: (p: number) => void;
}) {
  if (totalPages <= 1) return null;
  return (
    <div className="mt-4 flex items-center justify-between text-sm text-fg-muted">
      <button
        className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-muted disabled:opacity-40"
        disabled={page === 0}
        onClick={() => onChange(page - 1)}
      >
        Previous
      </button>
      <span>
        Page {page + 1} of {totalPages}
      </span>
      <button
        className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-surface-muted disabled:opacity-40"
        disabled={page >= totalPages - 1}
        onClick={() => onChange(page + 1)}
      >
        Next
      </button>
    </div>
  );
}
